import { invariant } from "@phosphor/utils";
import { useAtomValue } from "jotai";
import { createContext, useContext } from "react";
import { constAtom } from "#scripts/lib/jotai/constAtom.ts";
import { isAtom } from "#scripts/lib/jotai/isAtom.ts";
import { type UID, World, type WorldState } from "#scripts/vmc/WorldState.ts";

const WorldStateContext = createContext<WorldState | null>(null);

export const ProvideWorldState = WorldStateContext.Provider;

export function useComponentMaybe<T extends World.AnyComponentClass>(
  uid: UID | undefined | null,
  component: T,
): T["Type"] | undefined {
  const worldState = useContext(WorldStateContext);
  invariant(worldState, "WorldStateContext not found");
  const componentAtom = uid ? worldState.getComponentAtom(uid, component) : constAtom(undefined);
  invariant(isAtom(componentAtom), "Component atom is not an atom", { uid, component });
  return useAtomValue(componentAtom);
}

export function useWorldState(): WorldState {
  const worldState = useContext(WorldStateContext);
  invariant(worldState, "WorldStateContext not found");
  return worldState;
}

export function useComponent<T extends World.AnyComponentClass>(uid: UID, component: T): T["Type"] {
  const value = useComponentMaybe(uid, component);
  invariant(value, `Component (${component.key}) not found`, {
    uid,
    component,
  });
  return value;
}
export function useComponentForUIDMaybe<T extends World.AnyComponentClass>(
  uid: UID,
  lookup: World.AnyUIDComponentClass,
  component: T,
): T["Type"] | undefined {
  const { uidAtom } = useComponent(uid, lookup);
  invariant(isAtom(uidAtom), "UID atom is not an atom", { uid, lookup, component });
  const targetUID = useAtomValue(uidAtom);
  return useComponentMaybe(targetUID, component);
}

/**
 * Gets the value of a unique service from the world state. Throws if the unique
 * service is not found.
 */
export function useUnique<T extends World.AnyUniqueClass>(unique: T): T["Type"] {
  const worldState = useContext(WorldStateContext);
  invariant(worldState, "WorldStateContext not found");
  return worldState.getUniqueOrThrow(unique);
}

namespace _typeTest {
  class Co1 extends World.Component("test1")<Co1, { test1: 1 }>() {}
  class Co2 extends World.Component("test2")<Co2, { test2: 2 }>() {}
  class Co3 extends World.Component("test3")<Co3, { test3: 3 }>() {}

  class Entity1 extends World.Entity("entity1", {
    components: [Co1, Co2],
  })<Entity1>() {}

  function useTest(ent: UID<typeof Entity1>) {
    const _1: 1 | undefined = useComponent(ent, Co1)?.test1;
    const _2: 2 | undefined = useComponent(ent, Co2)?.test2;
    useComponent(ent, Co3);
  }

  function useTest2(ent: UID<[typeof Co1]>) {
    const _1: 1 | undefined = useComponent(ent, Co1)?.test1;
    useComponent(ent, Co2)?.test2;
    useComponent(ent, Co3);
  }
}
